import {
  __,
  always,
  assoc,
  assocPath,
  complement,
  concat,
  curry,
  filter,
  find,
  findLast,
  flip,
  gt,
  ifElse,
  isEmpty,
  isNil,
  map,
  mergeRight,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reduce,
  reject,
  pathOr
} from "ramda";

import { remoteFileDB as db, fileUrl as url } from "~/libs/pouch";
import { request } from "~/libs/request";

const FILE_SIZE_LIMIT = 800 * 1024 * 1024;

const STATES = {
  NEW: "Ausstehend",
  PENDING: "Laden...",
  UPLOADED: "Hochgeladen",
  ERROR: "Fehler"
};

const getStatusBySize = ifElse(
  gt(__, FILE_SIZE_LIMIT),
  always(STATES.ERROR),
  always(STATES.NEW)
);

export const revcat = flip(concat);
export const revmerge = flip(mergeRight);

export const isNewState = propEq("status", STATES.NEW);
export const isPendingState = propEq("status", STATES.PENDING);
export const isUploadedState = propEq("status", STATES.UPLOADED);
export const isErrorState = propEq("status", STATES.ERROR);

export const setNewState = assoc("status", STATES.NEW);
export const setPendingState = assoc("status", STATES.PENDING);
export const setUploadedState = assoc("status", STATES.UPLOADED);
export const setErrorState = assoc("status", STATES.ERROR);

export const attachmentListToMap = curry(list =>
  reduce((acc, attachment) => assoc(attachment.name, attachment, acc), {}, list)
);

export const filesToAttachments = curry(files =>
  Array.from(files).map(file => ({
    name: file.name,
    status: getStatusBySize(file.size),
    payload: {
      content_type: file.type,
      data: file
    }
  }))
);

export const getRevision = curry(id =>
  db
    .get(id)
    .then(propOr("", "_rev"), always(""))
);

export const getAttachments = curry(id =>
  db
    .get(id)
    .then(propOr({}, "_attachments"), always({}))
    .then(Object.entries)
    .then(
      map(([name, file]) => ({
        name,
        status: STATES.UPLOADED,
        payload: {
          content_type: file.content_type,
          data: file.data
        }
      }))
    )
);

export const removeAttachment = curry((id, name) =>
  db
    .get(id)
    .then(prop("_rev"))
    .then(rev => db.removeAttachment(id, name, rev))
);

export const removeDocument = curry(id =>
  db.get(id).then(({ _id, _rev }) => db.remove(_id, _rev))
);

export const putAttachment = curry((id, { name, payload }) =>
  db.upsert(id, assocPath(["_attachments", name], payload)).then(
    result => ({ name, result }),
    error => ({ name, error })
  )
);

export const filterNew = curry(attachments => filter(isNewState, attachments));

export const rejectUploaded = curry(attachments =>
  reject(isUploadedState, attachments)
);

export const rejectByName = curry((name, attachments) =>
  reject(propEq("name", name), attachments)
);

export const findByName = curry((name, attachments) =>
  find(propEq("name", name), attachments)
);

export const transformIfPending = curry((attachments, attachment) => {
  const pendingAttachment = findByName(attachment.name, attachments);

  if (isNil(pendingAttachment)) {
    return attachment;
  }

  return {
    ...pendingAttachment,
    status: "Laden..."
  };
});

export const uploadAttachment = curry((id, rev, { name, payload }) => {
  let path = `${url}/${id}/${name}`;

  let headers = {
    "X-Requested-With": "XMLHttpRequest",
    "Content-Type": payload.content_type || `application/octet-stream`,
    Accept: "application/json"
  };

  if (complement(isEmpty)(rev)) {
    headers["If-Match"] = rev;
    path += "?rev=" + rev;
  }

  return request(path, {
    method: "PUT",
    headers,
    body: payload.data
  }).then(
    result => ({ name, result }),
    error => ({ name, error })
  );
});

export const serialAttachmentUpload = curry(tasks =>
  tasks.reduce(
    (acc, task) =>
      acc.then(result => {
        const lastUpload = findLast(
          propSatisfies(complement(isNil), "result"),
          result
        );

        const rev = pathOr("", ["result", "rev"], lastUpload);

        return task(rev).then(r => result.concat(r));
      }),
    Promise.resolve([])
  )
);

export const getDocumentUrl = curry(id => `${url}/${id}`);

export const getAttachmentUrl = curry((base, name) => `${base}/${name}`);
